import { useState, useRef, useEffect } from "react";
import { ToolsShell, SensorNotice, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";
import { amplitudeToDb } from "./convertcore.js";

// Web Audio mic RMS → relative dB. NOT calibrated SPL (no phone PWA can
// be without hardware reference) — honest "relative" labeling.
export default function Decibel() {
  const [state, setState] = useState("idle");
  const [db, setDb] = useState(0);
  const [peak, setPeak] = useState(0);
  const refs = useRef({});

  const enable = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) { setState("unsupported"); return; }
    if (!window.isSecureContext) { setState("insecure"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      src.connect(analyser);
      const buf = new Float32Array(analyser.fftSize);
      refs.current = { ctx, stream, analyser, buf, raf: null, peak: 0 };
      const loop = () => {
        analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
        const rms = Math.sqrt(sum / buf.length);
        // Map RMS (ref 1.0 full-scale) to a positive relative scale.
        const raw = amplitudeToDb(rms || 1e-7, 1); // negative dBFS
        const rel = Math.max(0, Math.min(100, raw + 90)); // shift to readable 0-100
        setDb(rel);
        if (rel > refs.current.peak) { refs.current.peak = rel; setPeak(rel); }
        refs.current.raf = requestAnimationFrame(loop);
      };
      loop();
      setState("ok");
    } catch {
      setState("denied");
    }
  };

  useEffect(() => () => {
    const r = refs.current;
    if (r.raf) cancelAnimationFrame(r.raf);
    if (r.stream) r.stream.getTracks().forEach((t) => t.stop());
    if (r.ctx && r.ctx.state !== "closed") r.ctx.close();
  }, []);

  const pct = db; // already 0-100
  return (
    <ToolsShell title="Decibel Meter">
      {state !== "ok" && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <button onClick={enable} style={primaryBtn}>Enable microphone</button>
          <SensorNotice state={state === "idle" ? "prompt" : state} what="Microphone" />
        </div>
      )}
      {state === "ok" && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 24 }}>
          <div style={{ fontSize: 56, fontWeight: 800, color: TOOLS_ACCENT, lineHeight: 1 }}>
            {Math.round(db)}
          </div>
          <div style={{ fontSize: 13, color: "#9A9DBE", marginTop: 4 }}>relative level</div>
          <div style={{ width: "100%", height: 14, background: "#1C1E2E", borderRadius: 7, marginTop: 24, overflow: "hidden" }}>
            <div style={{ width: `${pct}%`, height: "100%", background: TOOLS_ACCENT, transition: "width 0.06s linear" }} />
          </div>
          <div style={{ fontSize: 13, color: "#9A9DBE", marginTop: 14 }}>Peak: {Math.round(peak)}</div>
          <button onClick={() => { refs.current.peak = 0; setPeak(0); }} style={{ marginTop: 10, background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, cursor: "pointer" }}>
            Reset peak
          </button>
          <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 18, textAlign: "center", maxWidth: 300 }}>
            This is a relative scale, not calibrated SPL — phone mics can't measure true decibels without a hardware reference. Useful for comparison, not legal/safety measurement.
          </p>
        </div>
      )}
    </ToolsShell>
  );
}

